import { MenuOption } from '../../models/menu-option.model';
import { OptionMenuEnum } from '../../enums/option-menu.enum';

/**
 * DTOs del endpoint:
 * - GET {servicesAPI}/api/user/permissions
 *
 * Nota:
 * - El back-end responde `PermissionResponseDto`:
 *   { permissions: number[], user: UserLoginDto }
 * - El `UserLoginDto` incluye `menuOptions`.
 */

export interface UserTypeDto {
  userTypeId: number;
  name: string;
  isSessionPermanent: boolean;
  sessionTime: number;
  /** Tiempo de inactividad en minutos (aviso y cierre de sesión si no hay actividad). */
  inactivityTime?: number;
  regex?: string;
  regexComment?: string;
}

export interface MenuOptionDto {
  screenId: number;
  /** Clave de la pantalla en BD (ej. "Home"). Vacío para módulos/submódulos. */
  key?: string;
  moduleType: number;
  title: string;
  path: string;
  icon: string;
  iconTypeId: number;
  isHomeScreen: boolean;
  order: number;
  homeOrder: number;
  subMenu: MenuOptionDto[];
}

export interface UserLoginDto {
  userId: number;
  /** Id del usuario de la sesión (el que inició sesión). Coincide con userId cuando no hay enmascaramiento. */
  userSessionId?: number;
  account: string;
  name: string;
  resetPassword: boolean;
  isMasked: boolean;
  language: string;
  userName: string;
  userTypeId: number;
  userType?: UserTypeDto | null;
  photo?: string | null;
  menuOptions: MenuOptionDto[];
  /** Código de estatus adicional (reservado; opcional). */
  status?: number | null;
}

/** Par (screenId, permissionId) para evaluar hasPermission(screen, permission) en el frontend. */
export interface ScreenPermissionDetailDto {
  screenId: number;
  permissionId: number;
  screenKey: string;
  permissionKey: string;
}

/**
 * Contrato real del API principal.
 */
export interface UserPermissionsResponseDto {
  permissions: number[];
  /** Detalle por pantalla y permiso; permite evaluar hasPermission(screen, permission). */
  screenDetails?: ScreenPermissionDetailDto[];
  user: UserLoginDto;
}

// ---- Type guards (validación runtime) ----

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

export function isMenuOptionDto(value: unknown): value is MenuOptionDto {
  if (!isObject(value)) return false;
  if (!isNumber(value['screenId'])) return false;
  if (!isNumber(value['moduleType'])) return false;
  if (!isString(value['title'])) return false;
  if (!isString(value['path'])) return false;
  if (!isString(value['icon'])) return false;
  if (!isNumber(value['iconTypeId'])) return false;
  if (!isBoolean(value['isHomeScreen'])) return false;
  if (!isNumber(value['order'])) return false;
  if (!isNumber(value['homeOrder'])) return false;
  if (!Array.isArray(value['subMenu'])) return false;
  return (value['subMenu'] as unknown[]).every(isMenuOptionDto);
}

export function isUserTypeDto(value: unknown): value is UserTypeDto {
  if (!isObject(value)) return false;
  const regexOk = value['regex'] == null ? true : isString(value['regex']);
  const regexCommentOk = value['regexComment'] == null ? true : isString(value['regexComment']);
  return (
    isNumber(value['userTypeId']) &&
    isString(value['name']) &&
    isBoolean(value['isSessionPermanent']) &&
    isNumber(value['sessionTime']) &&
    regexOk &&
    regexCommentOk
  );
}

export function isUserLoginDto(value: unknown): value is UserLoginDto {
  if (!isObject(value)) return false;
  const userType = value['userType'];
  const userTypeOk = userType == null ? true : isUserTypeDto(userType);

  const userSessionIdOk = value['userSessionId'] == null || isNumber(value['userSessionId']);
  return (
    isNumber(value['userId']) &&
    userSessionIdOk &&
    isString(value['account']) &&
    isString(value['name']) &&
    isBoolean(value['resetPassword']) &&
    isBoolean(value['isMasked']) &&
    isString(value['language']) &&
    isString(value['userName']) &&
    isNumber(value['userTypeId']) &&
    userTypeOk &&
    Array.isArray(value['menuOptions']) &&
    (value['menuOptions'] as unknown[]).every(isMenuOptionDto)
  );
}

export function isUserPermissionsResponseDto(value: unknown): value is UserPermissionsResponseDto {
  if (!isObject(value)) return false;
  const screenDetails = value['screenDetails'];
  const screenDetailsOk =
    screenDetails == null ||
    (Array.isArray(screenDetails) &&
      (screenDetails as unknown[]).every(
        (s: unknown) =>
          isObject(s) && isNumber((s as Record<string, unknown>)['screenId']) && isNumber((s as Record<string, unknown>)['permissionId'])
      ));
  return (
    Array.isArray(value['permissions']) &&
    (value['permissions'] as unknown[]).every(isNumber) &&
    screenDetailsOk &&
    isUserLoginDto(value['user'])
  );
}

// ---- Mappers (DTO -> Models) ----

export function mapMenuOptionDto(dto: MenuOptionDto): MenuOption {
  return new MenuOption({
    screenId: dto.screenId ?? 0,
    // moduleType es un enum numérico en el portal (OptionMenuEnum); se asigna por valor.
    moduleType: dto.moduleType as unknown as OptionMenuEnum,
    key: (dto.key ?? '').trim(),
    path: dto.path,
    title: dto.title,
    icon: dto.icon,
    homeScreen: dto.isHomeScreen,
    isOpen: false,
    isActive: false,
    order: dto.order,
    homeOrder: dto.homeOrder,
    subMenu: (dto.subMenu ?? []).map(mapMenuOptionDto),
  });
}

